import { isTestAccountUser } from '@officehub/shared';

/**
 * Drops our QA / fixture logins from a list people are PICKED from (assignee, owner, approver, mention
 * dropdowns). The rule is `isTestAccountUser` (the `isTestAccount` marker OR the #6188 email/name rule); an
 * unknown shape is NOT a test account, so a real person is never hidden by this. Works on anything that carries
 * an email / displayName / isTestAccount (users, members, people), so a screen that needs BOTH the full list
 * (to show the name of an already-assigned record) and the dropdown list derives the second from the first.
 *
 * `keep` lists ids/emails already in use on the screen; those rows stay even if they are test logins.
 *
 * Lives in its own module on purpose: several screens' tests replace the whole `hooks/useActiveUsers` module with a
 * mock, and a helper exported from there would vanish under them.
 */
export function excludeTestAccountUsers<
  T extends { email?: string | null; displayName?: string | null; name?: string | null; isTestAccount?: unknown; userId?: string | null; memberId?: string | null },
>(users: readonly T[], keep: readonly (string | null | undefined)[] = []): T[] {
  // `keep`: ids or emails the screen is ALREADY showing (a saved filter, an allow-list, the current assignee). A
  // dropdown that is also the only control showing or clearing a value must not make that value vanish.
  const kept = new Set(keep.filter((k): k is string => typeof k === 'string' && k !== '').map((k) => k.toLowerCase()));
  const isKept = (u: T) =>
    [u.userId, u.memberId, u.email].some((k) => typeof k === 'string' && kept.has(k.toLowerCase()));
  return users.filter((u) => !isTestAccountUser(u) || isKept(u));
}

/**
 * Splits rows into real people and test accounts by the same rule, for admin screens that show test accounts on
 * their own tab (still fully manageable there) instead of hiding them. Every row lands in exactly one group.
 */
export function partitionTestAccounts<T extends { email?: string | null; displayName?: string | null; name?: string | null; isTestAccount?: unknown }>(
  rows: readonly T[],
): { real: T[]; test: T[] } {
  const real: T[] = [];
  const test: T[] = [];
  for (const row of rows) (isTestAccountUser(row) ? test : real).push(row);
  return { real, test };
}
